import { useCallback, useState } from 'react';
import includes from 'lodash/includes';

import { ID, UUID } from '../../../../../../../../types';
import { ItemIndexSidebarTeamItemUser } from '../../../../../../components/sidebars/ItemIndexSidebar/components/ItemIndexSidebarTeam';
import {
  UsersTabsType,
  UsersTabType
} from '../../../../../../../users/usersTypes';

import { useCurrentUser } from '../../../../../../../../auth/hooks/useAuth/useAuth';

interface InviteUsersModalBodyOptions {
  availableTabs?: UsersTabsType;
  onSelectUser: (user: ItemIndexSidebarTeamItemUser) => void;
  onDeselectUser: (user: ItemIndexSidebarTeamItemUser) => void;
  onTabChange?: (tab: UsersTabType) => void;
}

function useInviteUsersModalBody({
  availableTabs,
  onSelectUser,
  onDeselectUser,
  onTabChange
}: InviteUsersModalBodyOptions) {
  const currentUser = useCurrentUser();

  const handleCheckUser = useCallback<
    (user: ItemIndexSidebarTeamItemUser, checked: boolean) => void
  >(
    (user: ItemIndexSidebarTeamItemUser, checked: boolean) =>
      checked ? onSelectUser(user) : onDeselectUser(user),
    [onSelectUser, onDeselectUser]
  );

  const handleCheckAllUsers = useCallback<
    (users: ItemIndexSidebarTeamItemUser[], checked: boolean) => void
  >(
    (users: ItemIndexSidebarTeamItemUser[], checked: boolean) =>
      checked
        ? users?.forEach((user) => onSelectUser(user))
        : users?.forEach((user) => onDeselectUser(user)),
    [onSelectUser, onDeselectUser]
  );

  const [openedTab, setOpenedTab] = useState<UsersTabType>(() => {
    if (
      includes(availableTabs, 'workers') &&
      currentUser.hasPermissions('read_workers_tab')
    ) {
      return 'workers';
    }

    if (
      includes(availableTabs, 'clients') &&
      currentUser.hasPermissions('read_clients_tab')
    ) {
      return 'clients';
    }

    return null;
  });

  const [fullName, setFullName] = useState<string>('');

  const [groupIds, setGroupIds] = useState<ID[]>([]);

  const [teamIds, setTeamIds] = useState<UUID[]>([]);

  const handleTabChange = useCallback<(tab: UsersTabType) => void>(
    (tab) => {
      setOpenedTab(tab);
      onTabChange?.(tab);
    },
    [setOpenedTab, onTabChange]
  );

  const handleChangeFullName = useCallback<
    (value: { fullName: string }) => void
  >((value) => setFullName(value?.fullName), [setFullName]);

  const handleChangeGroupIdsFilter = useCallback<
    (value: { groupIds: ID[] }) => void
  >((value) => setGroupIds(value?.groupIds), [setGroupIds]);

  const handleChangeTeamIdsFilter = useCallback<
    (value: { teamIds: UUID[] }) => void
  >((value) => setTeamIds(value?.teamIds), [setTeamIds]);

  return {
    fullName,
    groupIds,
    handleChangeFullName,
    handleChangeGroupIdsFilter,
    handleChangeTeamIdsFilter,
    handleCheckUser,
    handleCheckAllUsers,
    handleTabChange,
    openedTab,
    teamIds
  };
}

export default useInviteUsersModalBody;
